/**
 * <TutorLiveActionBar> — les gestes du centre du live, dans une barre flottante en bas au centre (molecule).
 *
 * Wajih, 04/11 : la tête du centre empilait trois lignes (l'élève, les gestes, les onglets). Les gestes
 * descendent ici, dans une pilule qui flotte au-dessus du contenu, comme la barre d'outils de Lovable ; en
 * haut ne restent que l'élève et les onglets.
 *
 * **Elle suit l'onglet ouvert** (`tutorLiveActionBarParts`, pur et testé) : sur une vue de l'activité, l'action
 * qu'attend son statut, « Montrer à l'élève », « Présenter » et « Plus » (son chrono et son statut sont sur son onglet,
 * en haut, Wajih, 05/11) ;
 * sur la visio ou les contrôles, « + Activité » seul. Le centre décide de ce qui s'y pose ; elle ne fait que le
 * montrer.
 *
 * **« + Activité » est toujours là, en tête** (Wajih, live du 04/11 au soir) : le geste le plus fréquent du live a
 * quitté la barre du bas pour celle-ci, sur chaque onglet. Il fait ressortir le champ de génération, en bas à droite,
 * pré-rempli sur l'activité ouverte. Un centre étroit prend les mots courts (`compact`), et la pilule passe à la ligne
 * plutôt que de couper un geste.
 *
 * **Toutes les actions en bas** (live du 04/11) : la barre du tableau blanc — l'encre, annuler, tout effacer,
 * « Générer un visuel », la bascule du visuel et son avis — y descend (`tools`, `<TutorAnnotationToolbar>`), en
 * tête de la pilule ; pendant une présentation, la barre de dessin et « Arrêter » y sont aussi (`tools` avec
 * `onStop`, ou `stop` seul sans dessin). Elle ne se pose jamais sur ce que voient les élèves : le centre lui
 * garde sa bande, sous la région partagée.
 *
 * **« + Activité » ne clignote pas** (Wajih, 05/11, après un halo qui respirait autour de lui sur la visio le 04/11) :
 * l'aplat de l'univers, en tête de la barre, suffit à le trouver ; un geste qui bat en continu distrait du live.
 *
 * Contrôlée : chaque geste repart.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UITutorActivityAction } from "../../../ports/primary/ui/types/tutorEleves"
import { Button } from "../../atoms/Buttons/Button"
import { type IconName } from "../../atoms/icons/Icon/iconRegistry"
import { shadowStyle } from "../../utils/shadow"
import { TutorAnnotationToolbar, type TutorAnnotationToolbarProps } from "../TutorAnnotationToolbar"
import {
    ACTIVITY_ACTIONS,
    ACTIVITY_SHOWN_LABEL,
    ACTIVITY_SHOWN_SHORT,
} from "../TutorLiveCenterHead/tutorActivityActions"

export type TutorLiveActionBarProps = {
    /** L'action qu'attend le statut (Valider, Redonner, Soumettre). */
    primary?: { action: UITutorActivityAction; onPress: () => void; pending?: boolean }
    /** « Montrer à l'élève » ; `shown` : il l'a devant lui. */
    show?: { onPress: () => void; pending?: boolean; shown?: boolean; pressableWhenShown?: boolean }
    /** « Présenter » (jamais « Arrêter » : pendant une présentation, la barre se retire). */
    present?: { onPress: () => void; pending?: boolean }
    /** « + Activité », en tête : le champ de génération ressort, pré-rempli. */
    onAdd?: () => void
    /** « Plus » : les autres gestes. */
    onOpenMenu?: () => void
    /** La barre de dessin : celle du tableau blanc, ou celle de la présentation (avec « Arrêter »). */
    tools?: TutorAnnotationToolbarProps
    /** « Arrêter » la présentation, quand elle n'a pas de barre de dessin. */
    stop?: { onPress: () => void; pending?: boolean }
    /** Un centre étroit : les mots courts. Défaut `false`. */
    compact?: boolean
    testID?: string
}

const TEXTS = {
    bar: "Gestes sur l'activité",
    present: "Présenter",
    stop: "Arrêter",
    stopLabel: "Arrêter la présentation",
    add: "Activité",
    addLabel: "Ajouter une activité",
    more: "Plus d'actions sur l'activité",
    menu: "Plus",
}

export function TutorLiveActionBar(props: TutorLiveActionBarProps) {
    const { primary, show, present, onAdd, onOpenMenu, tools, stop, compact = false, testID } = props
    const shown = show?.shown === true
    return (
        <View style={styles.pill} accessibilityRole="toolbar" accessibilityLabel={TEXTS.bar} testID={testID}>
            {onAdd != null && (
                <Action
                    label={TEXTS.addLabel}
                    shortLabel={TEXTS.add}
                    iconName="plus"
                    solid
                    pending={false}
                    onPress={onAdd}
                />
            )}
            {tools != null && <TutorAnnotationToolbar {...tools} />}
            {stop != null && (
                <Button
                    label={TEXTS.stop}
                    accessibilityLabel={TEXTS.stopLabel}
                    iconName="chalkboard-teacher"
                    color="danger"
                    size="sm"
                    loading={stop.pending === true}
                    onPress={stop.onPress}
                />
            )}
            {primary != null && (
                <Action
                    label={ACTIVITY_ACTIONS[primary.action].label}
                    iconName={ACTIVITY_ACTIONS[primary.action].icon}
                    solid
                    pending={primary.pending === true}
                    onPress={primary.onPress}
                />
            )}
            {show != null && (
                <Action
                    label={shown ? ACTIVITY_SHOWN_LABEL : ACTIVITY_ACTIONS.show.label}
                    shortLabel={compact ? (shown ? ACTIVITY_SHOWN_SHORT : ACTIVITY_ACTIONS.show.short) : undefined}
                    iconName={ACTIVITY_ACTIONS.show.icon}
                    done={shown}
                    pending={show.pending === true}
                    disabled={shown && show.pressableWhenShown !== true}
                    onPress={show.onPress}
                />
            )}
            {present != null && (
                <Action
                    label={TEXTS.present}
                    iconName="chalkboard-teacher"
                    pending={present.pending === true}
                    onPress={present.onPress}
                />
            )}
            {onOpenMenu != null && (
                <Button
                    label={TEXTS.menu}
                    iconName="ellipsis-h"
                    variant="ghost"
                    size="sm"
                    onPress={onOpenMenu}
                    accessibilityLabel={TEXTS.more}
                />
            )}
        </View>
    )
}

/** Un geste de la barre : le secondaire du livre, le principal pour l'action du statut. */
function Action(props: {
    label: string
    shortLabel?: string
    iconName?: IconName
    solid?: boolean
    pending: boolean
    disabled?: boolean
    /** L'action est déjà faite : la forme « déjà fait » (`<Button done>`). */
    done?: boolean
    onPress: () => void
}) {
    const { label, shortLabel, iconName, solid = false, pending, disabled = false, done = false, onPress } = props
    return (
        <Button
            label={shortLabel ?? label}
            accessibilityLabel={label}
            iconName={iconName}
            variant={solid ? "solid" : "outline"}
            size="sm"
            loading={pending}
            disabled={disabled}
            done={done}
            onPress={onPress}
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    /** La pilule : elle flotte (l'ombre des surfaces qui flottent), et passe à la ligne plutôt que de couper. */
    pill: {
        flexDirection: "row" as const,
        flexWrap: "wrap" as const,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        columnGap: theme.spacing["2"],
        rowGap: theme.spacing["2"],
        maxWidth: "100%",
        paddingVertical: theme.spacing["2"],
        paddingHorizontal: theme.spacing["3"],
        borderRadius: theme.radius.xl,
        backgroundColor: theme.colors["surface-floating"],
        borderWidth: 1,
        borderColor: theme.colors["border-subtle"],
        ...shadowStyle(theme, "floating"),
    },
}))
